<script setup lang="ts">
import { ref } from "vue";
import { __color } from "@zyzgroup/core-web";

// for merge ce shadow-root styles
defineOptions({
  customOptions: {}
});

const props = withDefaults(
  defineProps<{
    selected?: boolean;

    disabled?: boolean;

    isRounded?: boolean;

    label?: string;
    labelPadding?: string;

    colorBackground?: string;
    colorForeground?: string;
    colorDisabled?: string;

    /// reverse           背景色-前景色 互相转换
    /// only-foreground   只改变前景色
    colorChangeMode?: "reverse" | "only-foreground";

    boxWidth?: string;
    fontSize?: string;
  }>(),
  {
    selected: false,

    disabled: false,

    isRounded: true,

    label: "",
    labelPadding: "2px",

    colorBackground: "#ffffff",
    colorForeground: "#5a82f0",
    colorDisabled: "#cccccc",

    colorChangeMode: "reverse",

    boxWidth: "20px",
    fontSize: "14px"
  }
);

const emits = defineEmits<{
  (event: "change", value: boolean): void;
}>();

let realSelected = ref(props.selected);

let realDisabled = ref(props.disabled);

let realLabel = ref(props.label);

const getBackgroundColor = () => {
  switch (props.colorChangeMode) {
    case "only-foreground":
      return realDisabled.value ? props.colorDisabled : props.colorBackground;
    case "reverse":
    default:
      return realDisabled.value
        ? props.colorDisabled
        : realSelected.value
        ? props.colorForeground
        : props.colorBackground;
  }
};

const getBorderColor = () => {
  return realDisabled.value
    ? props.colorDisabled
    : __color.set(getBackgroundColor()).darken(0.2).toString();
};

const getForegroundColor = () => {
  switch (props.colorChangeMode) {
    case "only-foreground":
      return realDisabled.value ? props.colorBackground : props.colorForeground;
    case "reverse":
    default:
      return realSelected.value ? props.colorBackground : props.colorForeground;
  }
};

/// 主动变化
const click = () => {
  if (realDisabled.value) {
    return;
  }
  toggleValue();
  emits("change", realSelected.value);
};

/// 被动变化，不再 emit change
const setValue = (v: boolean) => {
  if (v !== realSelected.value) {
    realSelected.value = v;
  }
};

/// 被动变化，不再 emit change
const toggleValue = () => {
  realSelected.value = !realSelected.value;
};

const getValue = () => {
  return realSelected.value;
};

const enable = () => {
  realDisabled.value = false;
};

const disable = () => {
  realDisabled.value = true;
};

const setLabel = (label: string) => {
  realLabel.value = label;
};

defineExpose({ setValue, toggleValue, getValue, enable, disable, setLabel });
</script>

<template>
  <div
    class="boolean-box"
    :class="realDisabled ? 'disabled' : ''"
    @click="click"
  >
    <slot
      :selected="realSelected"
      :disabled="realDisabled"
      :label="realLabel"
    >
      <svg
        viewBox="0 0 24 24"
        fill="none"
        class="icon"
        :style="['width:' + props.boxWidth]"
      >
        <rect
          v-if="!props.isRounded"
          x="0"
          y="0"
          width="22"
          height="22"
          strke-width="1"
          :stroke="getBorderColor()"
          :fill="getBackgroundColor()"
        />
        <circle
          v-if="props.isRounded"
          cx="12"
          cy="12"
          r="11"
          strke-width="1"
          :stroke="getBorderColor()"
          :fill="getBackgroundColor()"
        />
        <path
          v-if="realSelected"
          d="m7 13l3 3 7-7"
          :stroke="getForegroundColor()"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
      <span
        v-if="label"
        :style="[
          'font-size:' + props.fontSize,
          'margin-left:' + props.labelPadding
        ]"
      >
        {{ label }}
      </span>
    </slot>
  </div>
</template>

<style scoped>
.boolean-box {
  position: relative;
  display: inline-flex;
  justify-content: flex-start;
  align-items: center;
  cursor: pointer;
}
.boolean-box.disabled {
  cursor: not-allowed;
  /* pointer-events: none; */
}
.icon {
  flex: 0 0 auto;
  box-sizing: border-box;
  display: inline-flex;
  justify-content: flex-start;
  align-items: center;
  transition: border-color 0.2s ease-in-out, background-color 0.2s ease-in-out;
}
</style>
